import { OPPOSITE_SIDES } from 'app/config/videogameSpecific.config';
import { sort } from 'app/utils/array';

type RoundForComputation = PickDefinedAndPartial<
  Round,
  'round_number' | 'winner_id' | 'winner_side',
  'winner_type'
>;

interface OpponentWithScore extends Pick<GameOpponent, 'id'> {
  score?: number | null;
}

// DATA
const getRoundGroupScore = (rounds: ComputedRound[], teamIndex: 0 | 1): number =>
  rounds.filter(round => round.teams[teamIndex].winner).length;

const getOvertimeScore = (groups: RoundGroup[], teamIndex: 0 | 1): number =>
  groups.reduce((sum, round) => sum + round.teams[teamIndex].score, 0);

const getSide = (
  round: ComputedRound | undefined,
  teamIndex: 0 | 1,
  isOvertime: boolean
): SideRound | null => {
  if (!round) {
    return null;
  }
  return isOvertime ? null : round.teams[teamIndex].side;
};

const getTotalScore = (opponents: OpponentWithScore[], teamId: number): number | null => {
  const opponent = opponents.find(opponent => opponent.id === teamId);
  if (opponent && 'score' in opponent) {
    return opponent.score ?? null;
  }
  return null;
};

const getTotalScores = (
  opponents: OpponentWithScore[],
  teamsIds: [number, number]
): [number | null, number | null] => [
  getTotalScore(opponents, teamsIds[0]),
  getTotalScore(opponents, teamsIds[1])
];

const getComputedRounds = (
  rounds: RoundForComputation[],
  teamIds: [number, number]
): ComputedRound[] =>
  rounds.map(({ round_number, winner_id, winner_type, winner_side }) => {
    const team1Winner = teamIds[0] === winner_id;
    const team2Winner = teamIds[1] === winner_id;
    const loserSide = OPPOSITE_SIDES[winner_side];

    return {
      round_number,
      winner_type,
      teams: [
        { side: team1Winner ? winner_side : loserSide, winner: team1Winner },
        { side: team2Winner ? winner_side : loserSide, winner: team2Winner }
      ]
    };
  });

const getRoundGroup = (
  rounds: RoundForComputation[],
  teamIds: [number, number],
  isOvertime = false
): RoundGroup => {
  const computedRounds = getComputedRounds(rounds, teamIds);

  return {
    rounds: computedRounds,
    teams: [
      {
        side: getSide(computedRounds.at(0), 0, isOvertime),
        score: getRoundGroupScore(computedRounds, 0)
      },
      {
        side: getSide(computedRounds.at(0), 1, isOvertime),
        score: getRoundGroupScore(computedRounds, 1)
      }
    ]
  };
};

const getOvertimeGroup = (
  rounds: RoundForComputation[],
  teamIds: [number, number],
  overtime_size: RoundsConfig['overtime_size']
): OvertimeGroup => {
  const roundGroups = new Array(Math.ceil(rounds.length / overtime_size))
    .fill(null)
    .map((_, index) => rounds.slice(index * overtime_size, (index + 1) * overtime_size))
    .map(group => getRoundGroup(group, teamIds, true));

  return {
    roundGroups,
    teamsScore: [getOvertimeScore(roundGroups, 0), getOvertimeScore(roundGroups, 1)]
  };
};

const getRounds = (
  rounds: RoundForComputation[],
  teamIds: [number, number],
  { overtime_size, round_group_size }: RoundsConfig
): GameRoundsScores => {
  const sortedRounds = sort(rounds, round => round.round_number);

  const firstHalfGroup = getRoundGroup(sortedRounds.slice(0, round_group_size), teamIds);
  const secondHalfGroup = getRoundGroup(
    sortedRounds.slice(round_group_size, round_group_size * 2),
    teamIds
  );

  const overtimeRounds = sortedRounds.slice(round_group_size * 2);
  const overTimeGroup = overtimeRounds.length
    ? getOvertimeGroup(overtimeRounds, teamIds, overtime_size)
    : undefined;

  return {
    halfgroups: [firstHalfGroup, secondHalfGroup],
    overtime: overTimeGroup
  };
};

export { getRounds, getTotalScores };
export type { RoundForComputation };
